import {
  GameType,
  TrackingPageGameBlock,
} from "@redotech/order-tracking-common/tracking-page-definition";
import type { KAPLAYCtx } from "kaplay";
import { TrackingPageGame, VictoryState } from "./game";
import type { GameCompleteCallback, GameControls } from "./game-controls";

// Multiple levels with varying difficulty
// Format: # = wall, @ = player, $ = box, . = target, + = box on target, * = player on target
export const CLEANUP_CREW_LEVELS: { name: string; level: string[] }[] = [
  // Level 1: Simple 2-box level
  {
    name: "Level 1: Simple 2-box level",
    level: [
      "########",
      "#      #",
      "# .  . #",
      "#  $$  #",
      "#   @  #",
      "#      #",
      "########",
    ],
  },

  // Level 2: Corner challenge
  {
    name: "Level 2: Corner challenge",
    level: [
      "#########",
      "#.     .#",
      "# #   # #",
      "#  $ $  #",
      "#   @   #",
      "#  $ $  #",
      "# #   # #",
      "#.     .#",
      "#########",
    ],
  },

  // Level 3: Narrow corridor
  {
    name: "Level 3: Narrow corridor",
    level: [
      "#        #",
      "#        #",
      "#.## #$#.#",
      "#   $$   #",
      "#    @   #",
      "# #$#### #",
      "#.      .#",
      "##########",
    ],
  },

  // Level 4: Cross pattern
  {
    name: "Level 4: Cross pattern",
    level: [
      "  #####  ",
      "  #. .#  ",
      "### $$###",
      "#. $@$ .#",
      "###$$ ###",
      "  #. .#  ",
      "  #####  ",
    ],
  },

  // Level 5: Snake pattern
  {
    name: "Level 5: Snake pattern",
    level: [
      "#########",
      "#.  $   #",
      "#### ## #",
      "#.$ $   #",
      "#  #@# ##",
      "#   $  .#",
      "# ## ####",
      "#.  $  .#",
      "#########",
    ],
  },

  // Level 6: Diamond formation
  {
    name: "Level 6: Diamond formation",
    level: [
      "#########",
      "####.####",
      "###   ###",
      "## $ $ ##",
      "#.  @  .#",
      "## $ $ ##",
      "###   ###",
      "####.####",
      "#########",
    ],
  },

  // Level 7: Winding path

  {
    name: "Level 7: Winding path",
    level: [
      "#      ###",
      "#  $ $ ###",
      "# $ $     ",
      "### # ##$ ",
      "##  #     ",
      "   $@ ####",
      " # ###  ..",
      "        ..",
      "######  ..",
    ],
  },
];

export const cleanupCrewGame: TrackingPageGame = {
  gameType: GameType.CLEANUP_CREW,
  initializeGame: initializeCleanupCrewGame,
  strings: {
    instructions:
      "Push boxes onto targets to complete the puzzle! Press R to reset.",
  },
  showControls: true,
};

interface CleanupCrewBoardState {
  gameGrid: string[][];
  boxes: { x: number; y: number }[];
  targets: { x: number; y: number }[];
  player: { x: number; y: number } | null;
  levelWidth: number;
  levelHeight: number;
  moves: number;
  cellSize: number;
  initialLevelData: string[];
}

// Calculate the optimal cell size to fit the level in the canvas
function calculateCellSize(
  k: KAPLAYCtx,
  width: number,
  height: number,
): number {
  const canvasWidth = k.width();
  const canvasHeight = k.height() - 40; // Leave space for UI elements

  // Add padding around the level (10% of canvas size)
  const paddingX = canvasWidth * 0.1;
  const paddingY = canvasHeight * 0.1;
  const availableWidth = canvasWidth - paddingX;
  const availableHeight = canvasHeight - paddingY;

  // Calculate maximum cell size that fits both dimensions with padding
  const maxCellWidth = Math.floor(availableWidth / width);
  const maxCellHeight = Math.floor(availableHeight / height);

  // Use the smaller of the two to ensure both dimensions fit
  const optimalSize = Math.min(maxCellWidth, maxCellHeight);

  // Ensure reasonable bounds: minimum 12px, maximum 60px for good gameplay
  return Math.max(12, Math.min(optimalSize, 60));
}

// Parse level from string format to game state
function parseLevelFromStrings(levelStrings: string[]) {
  const grid: string[][] = [];
  const levelBoxes: { x: number; y: number }[] = [];
  const levelTargets: { x: number; y: number }[] = [];
  let levelPlayer: { x: number; y: number } | null = null;

  for (const [y, levelString] of levelStrings.entries()) {
    const row: string[] = [];
    for (const [x, char] of levelString.split("").entries()) {
      switch (char) {
        case "#": // Wall
          row.push("#");
          break;
        case "@": // Player
          levelPlayer = { x, y };
          row.push(" "); // Player position becomes empty space
          break;
        case "$": // Box
          levelBoxes.push({ x, y });
          row.push(" "); // Box position becomes empty space
          break;
        case ".": // Target
          levelTargets.push({ x, y });
          row.push(".");
          break;
        case "+": // Box on target
          levelBoxes.push({ x, y });
          levelTargets.push({ x, y });
          row.push("."); // Target remains visible
          break;
        case "*": // Player on target
          levelPlayer = { x, y };
          levelTargets.push({ x, y });
          row.push("."); // Target remains visible
          break;
        default:
          row.push(" ");
          break;
      }
    }
    grid.push(row);
  }

  return { grid, levelBoxes, levelTargets, levelPlayer };
}

// Initialize the game state from level data
function initializeLevelState({
  k,
  selectedLevel,
  useRandomLevel,
}: {
  k: KAPLAYCtx;
  selectedLevel: number;
  useRandomLevel: boolean;
}): CleanupCrewBoardState {
  const levelIndex = useRandomLevel
    ? Math.floor(Math.random() * CLEANUP_CREW_LEVELS.length)
    : selectedLevel;
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  const levelStrings = CLEANUP_CREW_LEVELS[levelIndex].level;
  const initialLevelData = [...levelStrings]; // Store for reset

  const parsed = parseLevelFromStrings(levelStrings);
  const levelHeight = parsed.grid.length;
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  const levelWidth = parsed.grid[0].length;

  // Calculate optimal cell size for this level
  const cellSize = calculateCellSize(k, levelWidth, levelHeight);

  return {
    gameGrid: parsed.grid,
    boxes: parsed.levelBoxes,
    targets: parsed.levelTargets,
    player: parsed.levelPlayer,
    levelHeight,
    levelWidth,
    moves: 0,
    cellSize,
    initialLevelData,
  };
}

// Reset the level to initial state
function resetLevelState(state: CleanupCrewBoardState): CleanupCrewBoardState {
  const parsed = parseLevelFromStrings(state.initialLevelData);

  return {
    ...state,
    gameGrid: parsed.grid,
    boxes: parsed.levelBoxes,
    targets: parsed.levelTargets,
    player: parsed.levelPlayer,
    moves: 0,
  };
}

// Check if position is valid (not wall, not out of bounds)
function isValidPosition(
  state: CleanupCrewBoardState,
  x: number,
  y: number,
): boolean {
  if (x < 0 || x >= state.levelWidth || y < 0 || y >= state.levelHeight) {
    return false;
  }
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  return state.gameGrid[y][x] !== "#";
}

// Check if there's a box at position
function getBoxAt(
  state: CleanupCrewBoardState,
  x: number,
  y: number,
): { x: number; y: number } | undefined {
  return state.boxes.find((box) => box.x === x && box.y === y);
}

// Check if all boxes are on targets
function checkWinCondition(state: CleanupCrewBoardState): boolean {
  return state.boxes.every((box) =>
    state.targets.some((target) => target.x === box.x && target.y === box.y),
  );
}

// Move player and handle box pushing - returns new state or null if move is invalid
function movePlayerInState(
  state: CleanupCrewBoardState,
  dx: number,
  dy: number,
): CleanupCrewBoardState | null {
  if (!state.player) {
    return null;
  }

  const newX = state.player.x + dx;
  const newY = state.player.y + dy;

  // Check if new position is valid
  if (!isValidPosition(state, newX, newY)) {
    return null;
  }

  // Check if there's a box at the new position
  const boxAtNewPlayerPos = getBoxAt(state, newX, newY);
  let newBoxes = state.boxes;

  if (boxAtNewPlayerPos) {
    // Try to push the box
    const boxNewX = boxAtNewPlayerPos.x + dx;
    const boxNewY = boxAtNewPlayerPos.y + dy;

    // Check if box can be pushed
    if (
      !isValidPosition(state, boxNewX, boxNewY) ||
      getBoxAt(state, boxNewX, boxNewY)
    ) {
      return null; // Can't push box
    }

    // Create new boxes array with moved box
    newBoxes = state.boxes.map((box) =>
      box === boxAtNewPlayerPos ? { x: boxNewX, y: boxNewY } : box,
    );
  }

  // Return new state with moved player and potentially moved box
  return {
    ...state,
    player: { x: newX, y: newY },
    boxes: newBoxes,
    moves: state.moves + 1,
  };
}

// Draw the game state
function drawGame(k: KAPLAYCtx, state: CleanupCrewBoardState) {
  k.drawRect({
    width: k.width(),
    height: k.height(),
    color: k.rgb(50, 50, 50),
  });

  // Calculate offset to center the level
  const levelPixelWidth = state.levelWidth * state.cellSize;
  const levelPixelHeight = state.levelHeight * state.cellSize;
  const offsetX = (k.width() - levelPixelWidth) / 2;
  const offsetY = (k.height() - levelPixelHeight) / 2 + 20;

  // Draw grid
  for (let y = 0; y < state.levelHeight; y++) {
    for (let x = 0; x < state.levelWidth; x++) {
      const screenX = x * state.cellSize + offsetX;
      const screenY = y * state.cellSize + offsetY;
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      const cell = state.gameGrid[y][x];

      // Draw floor tiles (for non-wall spaces)
      if (cell === " " || cell === ".") {
        k.drawRect({
          pos: k.vec2(screenX, screenY),
          width: state.cellSize,
          height: state.cellSize,
          color: k.rgb(200, 180, 140), // Light brown/tan floor color
        });
      }

      // Draw walls
      if (cell === "#") {
        k.drawRect({
          pos: k.vec2(screenX, screenY),
          width: state.cellSize,
          height: state.cellSize,
          color: k.rgb(50, 50, 50), // Same color as background/out-of-bounds
        });
      }

      // Draw targets
      if (cell === ".") {
        k.drawCircle({
          pos: k.vec2(
            screenX + state.cellSize / 2,
            screenY + state.cellSize / 2,
          ),
          radius: state.cellSize / 3,
          color: k.rgb(0, 255, 0),
        });
      }
    }
  }

  // Draw boxes
  for (const box of state.boxes) {
    const screenX = box.x * state.cellSize + offsetX;
    const screenY = box.y * state.cellSize + offsetY;
    const isOnTarget = state.targets.some(
      (target) => target.x === box.x && target.y === box.y,
    );

    k.drawRect({
      pos: k.vec2(screenX + 2, screenY + 2),
      width: state.cellSize - 4,
      height: state.cellSize - 4,
      color: isOnTarget ? k.rgb(255, 255, 0) : k.rgb(139, 69, 19),
    });
  }

  // Draw player
  if (state.player) {
    const screenX = state.player.x * state.cellSize + offsetX;
    const screenY = state.player.y * state.cellSize + offsetY;

    k.drawCircle({
      pos: k.vec2(screenX + state.cellSize / 2, screenY + state.cellSize / 2),
      radius: state.cellSize / 3,
      color: k.rgb(255, 0, 0),
    });
  }
}

export function initializeCleanupCrewGame(
  k: KAPLAYCtx,
  gameCompleteCallback: GameCompleteCallback,
  blockProps: TrackingPageGameBlock,
): GameControls {
  if (blockProps.gameType !== GameType.CLEANUP_CREW) {
    throw new Error("Game type is not cleanup crew");
  }

  let gameState = initializeLevelState({
    k,
    selectedLevel: blockProps.gameSettings.selectedLevel,
    useRandomLevel: blockProps.gameSettings.useRandomLevel,
  });

  // Create score display
  const scoreText = k.add([
    k.text(`Moves: ${gameState.moves}`, { size: 16 }),
    k.pos(10, 10),
    k.color(255, 255, 255),
  ]);

  // Function to recalculate cell size when canvas resizes
  const recalculateCellSize = () => {
    const newCellSize = calculateCellSize(
      k,
      gameState.levelWidth,
      gameState.levelHeight,
    );
    if (newCellSize !== gameState.cellSize) {
      gameState = { ...gameState, cellSize: newCellSize };
    }
  };

  // Move player function that updates the game state
  const movePlayer = (dx: number, dy: number) => {
    const newState = movePlayerInState(gameState, dx, dy);
    if (newState) {
      gameState = newState;

      // Check win condition
      if (checkWinCondition(gameState)) {
        gameCompleteCallback({ victoryState: VictoryState.VICTORY });
      }
    }
  };

  // Reset level function
  const resetLevel = () => {
    gameState = resetLevelState(gameState);
    recalculateCellSize();
  };

  // Update level function for live refresh
  const updateLevel = (selectedLevel: number, useRandomLevel: boolean) => {
    gameState = initializeLevelState({ k, selectedLevel, useRandomLevel });
    recalculateCellSize();
  };

  // Handle keyboard input
  k.onKeyPress("left", () => movePlayer(-1, 0));
  k.onKeyPress("right", () => movePlayer(1, 0));
  k.onKeyPress("up", () => movePlayer(0, -1));
  k.onKeyPress("down", () => movePlayer(0, 1));

  // Handle WASD keys
  k.onKeyPress("a", () => movePlayer(-1, 0));
  k.onKeyPress("d", () => movePlayer(1, 0));
  k.onKeyPress("w", () => movePlayer(0, -1));
  k.onKeyPress("s", () => movePlayer(0, 1));

  // Handle reset key
  k.onKeyPress("r", resetLevel);

  // Game loop
  k.onUpdate(() => {
    drawGame(k, gameState);
    scoreText.text = `Moves: ${gameState.moves}`;
  });

  // Return controls for mobile
  return {
    moveUp: () => movePlayer(0, -1),
    moveDown: () => movePlayer(0, 1),
    moveLeft: () => movePlayer(-1, 0),
    moveRight: () => movePlayer(1, 0),
    stopMovement: () => {},
    resetLevel,
    updateLevel,
    onResize: recalculateCellSize,
  };
}
